import { useEffect, useRef } from 'react'

/**
 * Escape closes the dialog that is open.
 *
 * Every dialog in this product is dismissible — each one has a Close or a
 * Cancel — and four of eleven listened for Escape. The other seven could only
 * be left with the mouse, or by tabbing to the button: the keyboard shortcuts
 * sheet, opened from a keyboard shortcut, was one of them. There is no single
 * WCAG rule that says so; the convention is old enough that a dialog which
 * ignores Escape reads as stuck.
 *
 * One hook rather than seven copies of the same effect, and the reason is the
 * copies: `keydown` on `window` with a listener that is not removed leaves the
 * closed dialog's handler running, so the NEXT Escape closes something nobody
 * has open. Written once, that hazard is fixed once.
 *
 * `capture` is deliberate. A dialog is above everything else on the screen, so
 * its Escape belongs to it, not to whatever the keystroke would reach first —
 * an editor listening for Escape underneath must not answer for the dialog
 * over it.
 */
/**
 * And when two are open at once, the TOP one answers.
 *
 * `stopPropagation` in a capture listener on `window` does not stop the other
 * listeners on `window` — that is what `stopImmediatePropagation` is for, and
 * even that would only hand the keystroke to whichever hook happened to
 * register first. So one Escape reached every open dialog at once: the guided
 * tour over the changes drawer ended the tour AND closed the drawer under it,
 * which is the tour punishing somebody for skipping it.
 *
 * A number per open dialog, and the highest one wins. Not a stack that is
 * pushed and popped on every render: `close` is usually an inline arrow, so
 * the listener effect re-runs constantly, and a dialog that re-registered
 * would climb over the one on top of it. The number is taken once, when the
 * dialog OPENS, and the two effects below are split so that stays true.
 */
let opened = 0
const openDialogs = new Set<number>()

export function useEscape(open: boolean, close: () => void): void {
  const mine = useRef(0)

  useEffect(() => {
    if (!open) return
    mine.current = ++opened
    const id = mine.current
    openDialogs.add(id)
    return () => {
      openDialogs.delete(id)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Somebody else is on top of this one, and it is theirs to answer.
      for (const id of openDialogs) if (id > mine.current) return
      event.stopPropagation()
      close()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [open, close])
}
